import { GATEWAY_BRAIN_MCP_TRANSPORT, type BrainTransport } from "@m8t-stack/api-contract";

export const LOADER_START = "<!-- m8t:brain-loader:start -->";
export const LOADER_END = "<!-- m8t:brain-loader:end -->";
// Matches a LEGACY (pre-marker) loader by its distinctive opening (from
// targets/foundry/brain-loader.md) — far less likely to false-match a persona
// that merely uses "## Your second brain" as a heading.
const LEGACY_HEADER = "## Your second brain\n\nYou have a persistent second brain:";

/** Remove a previously-appended brain loader (marked or legacy) from instructions. */
export function stripBrainLoader(instructions: string): string {
  const s = instructions.indexOf(LOADER_START);
  if (s !== -1) {
    const e = instructions.indexOf(LOADER_END, s);
    const head = instructions.slice(0, s);
    const tail = e !== -1 ? instructions.slice(e + LOADER_END.length) : "";
    return (head + tail).replace(/\s+$/, "");
  }
  const h = instructions.lastIndexOf(LEGACY_HEADER);
  if (h !== -1) return instructions.slice(0, h).replace(/\s+$/, "");
  return instructions.replace(/\s+$/, "");
}

/** The loader text a live agent carries (without its markers), or null when it has none. */
export function liveBrainLoader(instructions: string): string | null {
  const s = instructions.indexOf(LOADER_START);
  if (s !== -1) {
    const e = instructions.indexOf(LOADER_END, s);
    return instructions.slice(s + LOADER_START.length, e !== -1 ? e : undefined).trim();
  }
  const h = instructions.lastIndexOf(LEGACY_HEADER);
  return h !== -1 ? instructions.slice(h).trim() : null;
}

/**
 * Fill `targets/foundry/brain-loader.md` for one link: keep only its transport's
 * block, then substitute the repo and branch. A link with no transport is a
 * legacy GitHub MCP link (see BrainLink.transport).
 */
export function renderBrainLoader(
  template: string,
  link: { repo: string; branch: string; transport?: BrainTransport },
): string {
  const keep = link.transport === GATEWAY_BRAIN_MCP_TRANSPORT ? "gateway-mcp-v1" : "legacy-github-mcp";
  const drop = keep === "gateway-mcp-v1" ? "legacy-github-mcp" : "gateway-mcp-v1";
  return unwrapTemplateBlock(stripTemplateBlock(template, drop), keep)
    .replaceAll("{{brain_repo}}", link.repo)
    .replaceAll("{{brain_branch}}", link.branch);
}

function stripTemplateBlock(template: string, name: string): string {
  const start = `<!-- m8t:${name}:start -->`;
  const end = `<!-- m8t:${name}:end -->`;
  const startAt = template.indexOf(start);
  if (startAt < 0) return template;
  const endAt = template.indexOf(end, startAt + start.length);
  if (endAt < 0) return template;
  return `${template.slice(0, startAt)}${template.slice(endAt + end.length)}`.trimEnd();
}

function unwrapTemplateBlock(template: string, name: string): string {
  return template
    .replace(`<!-- m8t:${name}:start -->`, "")
    .replace(`<!-- m8t:${name}:end -->`, "")
    .trim();
}

/** Strip any prior loader, then append `loaderText` wrapped in idempotency markers. */
export function appendBrainLoader(base: string, loaderText: string): string {
  const clean = stripBrainLoader(base);
  return `${clean}\n\n${LOADER_START}\n${loaderText.replace(/\s+$/, "")}\n${LOADER_END}\n`;
}
